/**
 * Focus data hook — a thin projection over the canonical collections store.
 *
 * The focus list is a Collection; the commitment is its **pinned** memberships
 * (`membershipSource === 'pinned'`), which is durable, purpose-built, and needs
 * no new schema (Doc 31 first law). Ordering is the collection's sort_order via
 * `reorderItems`. The "what's new since you last looked" beat of the re-focus
 * ritual is the store's own sightings delta (`getDelta` / `markCollectionViewed`).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCollectionsStore } from '@/stores/prioritization/collectionsStore';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import type { Item } from '@/types/prioritization';

/** A focus list is just a Collection; this name marks the one the app creates. */
export const FOCUS_COLLECTION_NAME = 'Focus';

export interface FocusEntry {
  item: Item;
  committed: boolean;
}

export function useFocusList(collectionId: string | null) {
  const collections = useCollectionsStore((s) => s.collections);
  const memberships = useCollectionsStore((s) => s.memberships);
  const fetchCollections = useCollectionsStore((s) => s.fetchFromSupabase);
  const items = useItemsStore((s) => s.items);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const userId = useUserStore((s) => s.userId);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([fetchCollections(), fetchItems()]).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchCollections, fetchItems]);

  /** Collections the app treats as focus lists (all of them — the user picks). */
  const focusLists = useMemo(
    () => [...collections].sort((a, b) => a.name.localeCompare(b.name)),
    [collections],
  );

  const collection = useMemo(
    () => focusLists.find((c) => c.id === collectionId) ?? null,
    [focusLists, collectionId],
  );

  /** Ordered entries: committed (pinned) first, then the standing pool. */
  const entries = useMemo<FocusEntry[]>(() => {
    if (!collectionId) return [];
    const byId = new Map(items.map((i) => [i.id, i]));
    const mine = memberships
      .filter((m) => m.collectionId === collectionId && m.membershipSource !== 'excluded')
      .sort((a, b) => a.sortOrder - b.sortOrder);
    const rows: FocusEntry[] = [];
    for (const m of mine) {
      const item = byId.get(m.itemId);
      if (!item) continue; // membership can outlive a deleted item
      rows.push({ item, committed: m.membershipSource === 'pinned' });
    }
    return [...rows.filter((r) => r.committed), ...rows.filter((r) => !r.committed)];
  }, [collectionId, memberships, items]);

  const committed = useMemo(() => entries.filter((e) => e.committed), [entries]);
  const pool = useMemo(() => entries.filter((e) => !e.committed), [entries]);

  /** Items in the workspace not yet on this list — the "add" candidates. */
  const candidates = useMemo(() => {
    const onList = new Set(
      memberships.filter((m) => m.collectionId === collectionId).map((m) => m.itemId),
    );
    return items.filter(
      (i) => !onList.has(i.id) && (!i.exit_status || i.exit_status === 'active'),
    );
  }, [items, memberships, collectionId]);

  return { loading, focusLists, collection, entries, committed, pool, candidates, userId };
}

/** Actions, kept separate so screens don't reach into the store directly. */
export function useFocusActions() {
  const createCollection = useCollectionsStore((s) => s.createCollection);
  const addItemToCollection = useCollectionsStore((s) => s.addItemToCollection);
  const removeItemFromCollection = useCollectionsStore((s) => s.removeItemFromCollection);
  const reorderItems = useCollectionsStore((s) => s.reorderItems);
  const pinItem = useCollectionsStore((s) => s.pinItem);
  const clearOverride = useCollectionsStore((s) => s.clearOverride);
  const markCollectionViewed = useCollectionsStore((s) => s.markCollectionViewed);
  const syncSightings = useCollectionsStore((s) => s.syncSightings);
  const getDelta = useCollectionsStore((s) => s.getDelta);
  const userId = useUserStore((s) => s.userId);

  const commit = useCallback(
    (collectionId: string, itemId: string) => pinItem(collectionId, itemId, userId ?? undefined),
    [pinItem, userId],
  );
  const uncommit = useCallback(
    (collectionId: string, itemId: string) => clearOverride(collectionId, itemId),
    [clearOverride],
  );
  const add = useCallback(
    (collectionId: string, itemId: string) =>
      addItemToCollection(collectionId, itemId, userId ?? undefined),
    [addItemToCollection, userId],
  );

  /** Close the ritual: record what was seen, then stamp the view. */
  const closeRitual = useCallback(
    async (collectionId: string, visibleItemIds: string[]) => {
      await syncSightings(collectionId, visibleItemIds);
      await markCollectionViewed(collectionId);
    },
    [syncSightings, markCollectionViewed],
  );

  return {
    createCollection,
    add,
    remove: removeItemFromCollection,
    reorderItems,
    commit,
    uncommit,
    closeRitual,
    getDelta,
  };
}
